import type React from 'react'

import {
  faMapMarkerAlt,
  faCheckDouble,
  faTags,
  faPalletBoxes,
  faClock,
  faTruck,
  faBoxOpenFull,
  faMobileAlt,
} from '@fortawesome/pro-regular-svg-icons'
import { FormattedNumber } from 'react-intl'
import { styled } from 'styled-components'

import {
  InventoryCategories,
  ContractDuration,
  ContractDurationTimeRange,
  ContractOrderFrequency,
  RetailPerOrder,
  SingleLineWithEllipsis,
} from '@b-stock/fe-core/bstock-next/components'
import {
  formatAddressShort,
  getNumberOfUnitsInLot,
  isLotUnmanifested,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedLotWithGroups,
  type OrchestratedListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  CurrencyStringCents,
  FormattedMessage,
  LotRollup,
  LotRollupHelpers,
  type LotRollupPercentages,
} from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Spacing,
  Typography,
} from '@b-stock/fe-core/design-system'

import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { Title } from '../../shared'
import { LotDescription } from '../LotDescription/LotDescription'
import {
  DescriptionRow,
  InfoSectionContainerWithoutBorder,
  GridIcon,
  GridInfo,
  GridTitle,
  InfoSectionContainerWithoutIconWithBorder,
  InfoSectionContainer,
} from './DescriptionRow'
import { ShipmentSize } from './info/ShipmentSize'

const ns = 'Listing.listingDetails'

const Wrapper = styled.div`
  margin-bottom: 1rem;
`

const ContractDurationContainer = styled.div`
  display: flex;
  ${Spacing.Primitive.XS.css('gap')}
`

const LotDescriptionContainer = styled.div`
  margin-top: 2rem;

  @media ${Breakpoints.max.medium} {
    display: none;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-gap: 0.5rem 1rem;
  grid-template-columns: 50% 50%;
  max-width: 40rem;
`
const GridContainerSection = styled.div`
  width: 100%;
`
const GridContainerInfo = styled(GridInfo)`
  width: 100%;
`
const StyledSingleLineWithEllipsis = styled(SingleLineWithEllipsis)`
  grid-area: info;
  ${Typography.Subtitle1}
  @media ${Breakpoints.max.small} {
    ${Typography.Subtitle2}
  }
`

const RollupInfo = <T extends LotRollupPercentages, V extends string>({
  lot,
  rollupHelper,
  intlKey,
  ellipsis,
}: {
  lot: OrchestratedLotWithGroups
  rollupHelper: (
    lot: Partial<OrchestratedLotWithGroups>
  ) => React.ComponentPropsWithoutRef<typeof LotRollup<T, V>>
  intlKey: string
  ellipsis?: boolean
}) => {
  const Info = ellipsis ? StyledSingleLineWithEllipsis : GridContainerInfo
  return (
    <GridContainerSection data-testid={`listing-description-${intlKey}`}>
      <GridTitle>
        <FormattedMessage id={`${ns}.${intlKey}.title`} />
      </GridTitle>
      <Info>
        <LotRollup<T, V>
          {...rollupHelper(lot)}
          showPercentages
          numShown={ellipsis ? undefined : 4}
        />
      </Info>
    </GridContainerSection>
  )
}

const buildOptionalRow = <T extends LotRollupPercentages, V extends string>({
  lot,
  data,
  rollupHelper,
  attributeKey,
  intlKey,
}: {
  lot: OrchestratedLotWithGroups
  data: T[] | undefined
  rollupHelper: (
    lot: Partial<OrchestratedLotWithGroups>
  ) => React.ComponentPropsWithoutRef<typeof LotRollup<T, V>>
  attributeKey: `item.${string}`
  intlKey: string
}) => {
  const attributeOrder = lot.attributeOrder || []
  const isUnmanifested = isLotUnmanifested(lot)
  const shouldShow = isUnmanifested
    ? (data?.length ?? 0) > 0
    : attributeOrder.includes(attributeKey)

  return shouldShow ? (
    <RollupInfo
      rollupHelper={rollupHelper}
      lot={lot}
      intlKey={intlKey}
      ellipsis
    />
  ) : null
}

export const ListingDescription = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const { trackButtonClicked } = useAnalytics()

  const handleLinkClicked = (href: string) => {
    trackButtonClicked({
      screenName: 'listing_detail_page',
      buttonName: 'external_link',
      source: AnalyticsSource.HOME_PORTAL,
      entity_type: AnalyticsEntityType.BUYER,
      listing_id: listing._id,
      seller_display_name: listing.seller.account.displayName,
      storefront_name: listing.seller.storefront?.name,
      storefront_id: listing.storefrontId,
      link_url: href,
    })
  }

  const isContract = listing.listingType === 'CONTRACT'

  const sellerLocation = formatAddressShort(listing.lot.location, true)

  const manifestUnits = getNumberOfUnitsInLot(listing.lot) ?? 0
  const manifestTotalMSRP = listing.lot.retailValue
  const msrpOrCogs = listing.contract?.bidUnit === 'COGS' ? 'COGS' : 'MSRP'

  const modelRow = buildOptionalRow({
    lot: listing.lot,
    data: listing.lot.models,
    attributeKey: 'item.attributes.model',
    intlKey: 'models',
    rollupHelper: LotRollupHelpers.models,
  })

  const manufacturerRow = buildOptionalRow({
    lot: listing.lot,
    data: listing.lot.manufacturers,
    attributeKey: 'item.attributes.item.manufacturer',
    intlKey: 'manufacturers',
    rollupHelper: LotRollupHelpers.manufacturers,
  })

  const capacityRow = buildOptionalRow({
    lot: listing.lot,
    data: listing.lot.capacities,
    attributeKey: 'item.attributes.item.capacity',
    intlKey: 'capacities',
    rollupHelper: LotRollupHelpers.capacities,
  })

  const carrierRow = buildOptionalRow({
    lot: listing.lot,
    data: listing.lot.carriers,
    attributeKey: 'item.attributes.item.carrier',
    intlKey: 'carrier',
    rollupHelper: LotRollupHelpers.carriers,
  })

  const sellerGradeRow = buildOptionalRow({
    lot: listing.lot,
    data: listing.lot.mobileDevice?.sellerGrades,
    attributeKey: 'item.mobileDevice.sellerGrade',
    intlKey: 'sellerGrade',
    rollupHelper: LotRollupHelpers.sellerGrades,
  })

  const enableMemoryRollupVisibility = useFlag(
    FeatureFlags.ENABLE_MEMORY_ROLLUP_VISIBILITY
  )

  // LotDto doesn't yet have `memories` — backend support is pending.
  // When backend ships, remove this assertion and use the real typed field.
  const lotWithMemories = listing.lot as OrchestratedLotWithGroups &
    LotRollupHelpers.LotWithMemories
  const memoryRow = enableMemoryRollupVisibility
    ? buildOptionalRow<LotRollupHelpers.MemoryDto, string>({
        lot: lotWithMemories,
        data: lotWithMemories.memories,
        attributeKey: 'item.attributes.memory',
        intlKey: 'memories',
        rollupHelper: () => LotRollupHelpers.memories(lotWithMemories),
      })
    : null

  const sellerConditionGradeFlag = useFlag(
    FeatureFlags.ENABLE_LDP_SELLER_CONDITION_GRADE_ENHANCEMENT
  )
  const storefrontId = listing.seller.storefront?._id
  const storefrontSlug = listing.seller.storefront?.slug
  const isStorefrontWhitelisted =
    !!sellerConditionGradeFlag &&
    sellerConditionGradeFlag.enabled &&
    ((!!storefrontId &&
      !!sellerConditionGradeFlag.includedStorefrontIds?.includes(
        storefrontId
      )) ||
      (!!storefrontSlug &&
        !!sellerConditionGradeFlag.includedStorefrontSlugs?.includes(
          storefrontSlug
        )))
  const showCondition = isStorefrontWhitelisted ? sellerGradeRow === null : true

  return (
    <Wrapper data-testid="listing-description-section">
      <DescriptionRow
        testId="listing-description-category"
        icon={faTags}
        title={<FormattedMessage id={`${ns}.category.title`} />}
        info={<InventoryCategories lot={listing.lot} />}
      />

      <InfoSectionContainer>
        <GridIcon icon={faCheckDouble} />
        <GridContainer>
          {showCondition && (
            <RollupInfo
              lot={listing.lot}
              rollupHelper={LotRollupHelpers.itemConditions}
              intlKey="condition"
            />
          )}
          {sellerGradeRow}
        </GridContainer>
      </InfoSectionContainer>
      <DescriptionRow
        icon={faPalletBoxes}
        title={<FormattedMessage id={`${ns}.inventoryType.title`} />}
        info={
          <LotRollup
            {...LotRollupHelpers.inventoryTypes(listing.lot)}
            showPercentages
          />
        }
        testId="listing-description-inventory-type"
      />
      {!isContract && (manifestUnits || manifestTotalMSRP) && (
        <DescriptionRow
          icon={faBoxOpenFull}
          title={<FormattedMessage id={`${ns}.manifestSummary.title`} />}
          info={(() => {
            const hasUnits = manifestUnits && manifestUnits > 0
            const hasMSRP = manifestTotalMSRP && manifestTotalMSRP > 0

            if (hasUnits && hasMSRP) {
              return (
                <FormattedMessage
                  id={`${ns}.manifestSummary.description`}
                  values={{
                    units: <FormattedNumber value={manifestUnits} />,
                    msrp: (
                      <CurrencyStringCents
                        value={manifestTotalMSRP}
                        currency={listing.currency || 'USD'}
                      />
                    ),
                  }}
                />
              )
            } else if (hasMSRP) {
              return (
                <FormattedMessage
                  id={`${ns}.manifestSummary.MSRP`}
                  values={{
                    msrp: (
                      <CurrencyStringCents
                        value={manifestTotalMSRP}
                        currency={listing.currency || 'USD'}
                      />
                    ),
                  }}
                />
              )
            } else if (hasUnits) {
              return (
                <FormattedMessage
                  id={`${ns}.manifestSummary.units`}
                  values={{
                    units: <FormattedNumber value={manifestUnits} />,
                  }}
                />
              )
            }
            return null
          })()}
        />
      )}
      {isContract && listing.contract && (
        <DescriptionRow
          icon={faClock} // adjust to proper icon (copied from older design)
          title={<FormattedMessage id={`${ns}.contractDuration.title`} />}
          info={
            <ContractDurationContainer>
              <ContractDurationTimeRange
                contract={listing.contract}
                variant="verbose"
              />
              {` | `}
              <ContractDuration contract={listing.contract} />
            </ContractDurationContainer>
          }
          testId="listing-description-contract-duration"
        />
      )}
      <DescriptionRow
        icon={faMapMarkerAlt}
        title={<FormattedMessage id={`${ns}.location.title`} />}
        info={sellerLocation}
        testId="listing-description-location"
      />
      {!isContract && (
        <DescriptionRow
          icon={faTruck}
          title={<FormattedMessage id={`${ns}.shipmentSize.title`} />}
          info={<ShipmentSize listing={listing} />}
          testId="listing-description-shipment-size"
        />
      )}
      {isContract && listing.contract && (
        <>
          <DescriptionRow
            icon={faTruck}
            title={
              <FormattedMessage
                id={`${ns}.estRetailPerOrder.title`}
                values={{ msrpOrCogs }}
              />
            }
            info={
              <RetailPerOrder
                contract={listing.contract}
                currency={listing.currency || 'USD'}
              />
            }
            testId="listing-description-retail-per-order"
          />
          <DescriptionRow
            icon={faTruck}
            title={<FormattedMessage id={`${ns}.estOrderFrequency.title`} />}
            info={<ContractOrderFrequency contract={listing.contract} />}
            testId="listing-description-order-frequency"
          />
        </>
      )}
      {modelRow || manufacturerRow ? (
        <InfoSectionContainerWithoutBorder>
          <GridIcon icon={faMobileAlt} />
          <GridContainer>
            {modelRow}
            {manufacturerRow}
          </GridContainer>
        </InfoSectionContainerWithoutBorder>
      ) : null}

      {capacityRow || carrierRow || memoryRow ? (
        <InfoSectionContainerWithoutIconWithBorder>
          <span />
          <GridContainer>
            {memoryRow}
            {capacityRow}
            {carrierRow}
          </GridContainer>
        </InfoSectionContainerWithoutIconWithBorder>
      ) : null}
      {listing.lot.description ? (
        <LotDescriptionContainer>
          <Title>
            <FormattedMessage id={`${ns}.lotDescription.title`} />
          </Title>
          <LotDescription
            description={listing.lot.description}
            linkClickedHandler={handleLinkClicked}
          />
        </LotDescriptionContainer>
      ) : null}
    </Wrapper>
  )
}
